import { AlertDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  /** Ação destrutiva (ex.: encerrar, excluir): botão em vermelho. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
};

/**
 * Confirmação de ação que não se desfaz. O foco começa em "Cancelar" (o caminho seguro) e o
 * diálogo só fecha sozinho ao cancelar: quem confirma decide quando fechar (após a API responder).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  busy,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-(--z-index-overlay) bg-overlay motion-safe:data-[state=open]:animate-[fade-in_150ms_var(--ease-out)]" />
        <AlertDialog.Content className="fixed top-1/2 left-1/2 z-(--z-index-modal) w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-surface-raised p-5 shadow-md outline-none motion-safe:data-[state=open]:animate-[rise-in_200ms_var(--ease-out)]">
          <AlertDialog.Title className="text-lg font-bold">{title}</AlertDialog.Title>
          {/* div (não p): a descrição pode ter listas. */}
          <AlertDialog.Description asChild>
            <div className="mt-2 text-sm text-muted-foreground">{description}</div>
          </AlertDialog.Description>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialog.Cancel asChild>
              <Button variant="outline" disabled={busy}>
                Cancelar
              </Button>
            </AlertDialog.Cancel>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              loading={busy}
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
